<template>
  <div class="h-full flex flex-col">
    <template v-if="!$vuetify.breakpoint.smAndDown">
      <v-data-table
      :headers="tableHeaders"
      :items="roles"
      class="table-radius border border-gray-200"
      item-key="_id"
      :key="rolesTableKey"
      :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
      :options.sync="internalOptions"
      :server-items-length="totalRoles"
      :loading="loadTable"
      show-select
      v-model="selected"
    >
      <!-- Use specific column templates instead of overriding the entire row -->
      <!-- This allows Vuetify to automatically handle the checkbox column -->

      <template v-slot:item.role="{ item }">
        <td class="px-4 py-3">{{ item.role }}</td>
      </template>

      <template v-slot:item.privileges="{ item }">
        <td class="px-4 py-3">
          <div class="d-flex item-center">
            {{ formatNumber(item.extraPrivileges?.length || 0) }}
            <v-icon
              v-if="item.extraPrivileges?.length > 0"
              size="22"
              left
              color="primary"
              v-tooltip="{
                text: `${getPrivilegeTooltip(item)}`,
                width: '300px',
                height: '400px',
                trigger: 'click',
                bottom: true
              }"
              class="ml-2 cursor-pointer"
            >
              mdi-information-variant-circle-outline
            </v-icon>
          </div>
        </td>
      </template>

      <template v-slot:item.permissions="{ item }">
        <td class="px-4 py-3">
          <div class="d-flex item-center">
            {{ formatNumber(getPermissionCount(item)) }}
            <v-icon
              v-if="getPermissionCount(item) > 0"
              size="22"
              left
              color="primary"
              v-tooltip="{
                text: `${getPermissionTooltip(item)}`,
                width: '300px',
                height: '400px',
                trigger: 'click'
              }"
              class="ml-2 cursor-pointer"
            >
              mdi-information-variant-circle-outline
            </v-icon>
          </div>
        </td>
      </template>

      <template v-slot:item.modulePermissions="{ item }">
        <td class="px-4 py-3">
          <div class="d-flex item-center">
            {{ formatNumber(getModulePermissionCount(item)) }}
            <v-icon
              v-if="getModulePermissionCount(item) > 0"
              size="22"
              left
              color="primary"
              v-tooltip="{
                text: `${getModulePermissionTooltip(item)}`,
                width: '300px',
                height: '400px',
                trigger: 'click'
              }"
              class="ml-2 cursor-pointer"
            >
              mdi-information-variant-circle-outline
            </v-icon>
          </div>
        </td>
      </template>

      <template v-slot:item.action="{ item }">
        <td class="bord flex items-center justify-end py-2">
          <!-- IAM policy statements configuration trigger -->
          <!-- v-iam-disabled="'organization.roles.update'" -->
          <v-btn
            icon
            small
            color="secondary"
            class="mr-2"
            v-tooltip="{ text: 'Edit IAM Policies (JSON)', top: true }"
            @click.stop="$emit('configure-iam', item)"
          >
            <v-icon small>mdi-code-json</v-icon>
          </v-btn>
          <action-button
            :item="item"
            :canEdit="hasIamPermission(iamStatements, 'organization.roles.update')"
            :showEditButton="hasIamPermission(iamStatements, 'organization.roles.update')"
            :showDeleteButton="hasIamPermission(iamStatements, 'organization.roles.delete')"
            :canDelete="hasIamPermission(iamStatements, 'organization.roles.delete')"
            class="justify-end mx-5"
            :handleEdit="() => editRole(item)"
            :handleDelete="() => deleteRole(item._id)"
          />
        </td>
      </template>

      <template v-for="(header, index) in tableHeaders" v-slot:[`header.${header.value}`]>
        <span :key="index" class="inline-flex items-center">
          <span v-tooltip="{ text: header.text, top: true }">{{ header.text }}</span>
          <v-icon
            v-if="header.value === 'permissions'"
            small
            class="ml-1 text-gray-400 cursor-pointer"
            v-tooltip="{
              text: 'Permissions define access rights to different modules',
              bottom: true
            }"
          >
            mdi-information-outline
          </v-icon>
        </span>
      </template>
    </v-data-table>
    </template>
    <PrevilageTable_mobile v-else />

    <v-dialog v-model="showDeleteConfirmation" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline mt-1 text-h5 justify-center text-[#6B6D78]"
          >Confirm Deletion</v-card-title
        >
        <v-card-text class="text-center mt-2">
          Are you sure you want to delete this role?
        </v-card-text>
        <v-card-actions class="flex justify-center mb-2">
          <v-btn outlined class="mr-4" @click="showDeleteConfirmation = false">
            <span class="text-black">Cancel</span>
          </v-btn>
          <v-btn
            :color="$vuetify.theme.currentTheme.primary"
            class="white--text"
            @click="confirmDeleteRole"
          >
            Yes, Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { hasPermission, convertToTitleCase } from '@/utils/common'
import permissionData from '@/utils/permissions'
import ActionButton from '../../ActionButton'
import { colors } from 'vuetify/lib'
import { axiosUrl } from '@/utils/workforceApi'
import { hasIamPermission } from '@/utils/iamEvaluator'

import PrevilageTable_mobile from './PrevilageTable_mobile.vue'

export default {
  name: 'PrevilageTable',
  components: {
    ActionButton,
    PrevilageTable_mobile
  },
  provide() {
    return {
      parentTable: this
    }
  },
  props: {
    roles: {
      type: Array,
      default: () => []
    },
    loadTable: {
      type: Boolean,
      default: false
    },
    totalRoles: {
      type: Number,
      default: 0
    }
  },
  emits: ['edit-role', 'role-deleted', 'update:options'],
  data() {
    return {
      userData: this.$store.state.wfmPermissions,
      internalOptions: { sortBy: [], sortDesc: [], itemsPerPage: 10, page: 1 },
      showDeleteConfirmation: false,
      roleToDeleteId: null,
      rolesTableKey: 0,
      additionalPermissions: [],
      selected: []
    }
  },
  watch: {
    internalOptions: {
      handler(newOptions) {
        // Emit the options change to the parent component
        this.$emit('update:options', newOptions)
      },
      deep: true,
      immediate: true
    }
  },
  async mounted() {
    await this.populatePermissions()
  },
  methods: {
    hasIamPermission,
    formatNumber(number) {
      return number < 10 ? `0${number}` : number
    },
    titleCase(str) {
      return convertToTitleCase(str)
    },
    async populatePermissions() {
      try {
        const response = await this.$axios.get(axiosUrl.role.populatePermissions)
        this.additionalPermissions = response.data.privileges
      } catch (error) {
        console.error('Error fetching privileges:', error)
      }
    },
    getPermissionCount(role) {
      return role.permissions ? role.permissions.length : 0
    },
    getPrivilegeTooltip(item) {
      if (!item.extraPrivileges || item.extraPrivileges.length === 0) return 'No privileges'

      const privilegeNames = this.getPrivilegeNames(item.extraPrivileges)
      return privilegeNames
        .map(
          (name, index) =>
            `<div style="text-align:left; margin-bottom: 8px; padding: 4px 0; border-bottom: 1px solid rgba(200,250,255,0.2);">
          <strong>${index + 1}. ${this.titleCase(name)}</strong>
        </div>`
        )
        .join('')
    },
    getPermissionTooltip(item) {
      if (!item.permissions || item.permissions.length === 0) return 'No permissions'

      const permissionDetails = this.getPermissionDetails(item.permissions)
      return permissionDetails
        .map(
          (permission, index) =>
            `<div style="text-align:left; margin-bottom: 8px; padding: 4px 0; border-bottom: 1px solid rgba(255,255,255,0.2);">
                <strong >${index + 1}. ${this.titleCase(permission.module)}</strong>
                <p>${this.formatPermissionWithColors(permission.permissions)}</p>
              </div>`
        )
        .join('')
    },
    getModulePermissionCount(role) {
      if (!role.modulePermissions) return 0
      let count = 0
      Object.keys(role.modulePermissions).forEach((moduleKey) => {
        const perms = role.modulePermissions[moduleKey] || {}
        if (perms.read || perms.add || perms.edit || perms.delete) {
          count++
        }
      })
      return count
    },
    getModulePermissionTooltip(item) {
      if (!item.modulePermissions) return 'No module permissions'
      const details = []
      Object.keys(item.modulePermissions).forEach((moduleKey) => {
        const perms = item.modulePermissions[moduleKey] || {}
        const selected = []
        if (perms.read) selected.push('Read')
        if (perms.add) selected.push('Add')
        if (perms.edit) selected.push('Edit')
        if (perms.delete) selected.push('Delete')
        if (selected.length > 0) {
          details.push({
            module: moduleKey.toLowerCase().replace(/_/g, ' '),
            permissions: selected.join(', ')
          })
        }
      })

      if (details.length === 0) return 'No module permissions'

      return details
        .map(
          (d, index) =>
            `<div style="text-align:left; margin-bottom: 8px; padding: 4px 0; border-bottom: 1px solid rgba(255,255,255,0.2);">
                <strong>${index + 1}. ${this.titleCase(d.module)}</strong>
                <p>${this.formatPermissionWithColors(d.permissions)}</p>
              </div>`
        )
        .join('')
    },
    getPrivilegeNames(privilegeKeys) {
      if (!privilegeKeys || privilegeKeys.length === 0) return []

      return privilegeKeys.map((key) => {
        const privilege = this.additionalPermissions.find((p) => p.key === key)
        return privilege ? privilege.value : key
      })
    },
    getPermissionDetails(permissions) {
      if (!permissions || permissions.length === 0) return []
      const permissionMap = {}

      permissions.forEach((permission) => {
        const [module, type] = permission.split(':')
        if (!permissionMap[module]) {
          permissionMap[module] = []
        }
        permissionMap[module].push(type)
      })

      const x = Object.keys(permissionMap).map((module, index) => {
        return {
          module,
          permissions: permissionMap[module].join(', ')
        }
      })
      return x
    },
    formatPermissionWithColors(permissionString) {
      const colorMap = {
        read: '#2196F3', // Brighter Blue
        write: '#4CAF50', // Green
        add: '#4CAF50', // Green
        edit: '#FFC107', // Amber
        delete: '#F44336' // Red
      }

      const permissions = permissionString.split(',').map((perm) => perm.trim())

      return permissions
        .map((permission) => {
          const lowerPermission = permission.toLowerCase()
          const color = colorMap[lowerPermission] || '#424242'
          return `<span style="display: inline-block;  color: ${color}; padding: 2px 4px; font-size: 12px; margin: 0px;">${this.titleCase(
            permission
          )}</span>`
        })
        .join(' ')
    },
    editRole(roleData) {
      this.$emit('edit-role', roleData)
    },
    deleteRole(roleId) {
      this.roleToDeleteId = roleId
      this.showDeleteConfirmation = true
    },
    async confirmDeleteRole() {
      try {
        const res = await this.$axios.delete(`${axiosUrl.role.deleteRoles}/${this.roleToDeleteId}`)
        if (res.data.success) {
          // Emit an event to let the parent component know a role was deleted
          this.$emit('role-deleted', this.roleToDeleteId)
        } else {
          console.error('Error deleting role:', res.data.message)
        }
        this.showDeleteConfirmation = false
      } catch (error) {
        console.error('Error deleting role:', error)
      }
    }
  },
  computed: {
    iamStatements() {
      return this.$store.state.wfmStatements || []
    },
    canDelete() {
      return hasPermission(this.userData, permissionData.settingDelete)
    },
    canEdit() {
      return hasPermission(this.userData, permissionData.settingEdit)
    },
    tableHeaders() {
      // Note: We don't need to add a header for the checkbox column
      // Vuetify automatically adds it when show-select is used
      return [
        {
          text: 'Role',
          value: 'role',
          class: 'table-header',
          width: '25%'
        },
        {
          text: 'Privileges',
          value: 'privileges',
          class: 'table-header',
          width: '20%',
          sortable: false
        },
        {
          text: 'Permissions',
          value: 'permissions',
          class: 'table-header',
          width: '20%',
          sortable: false
        },
        {
          text: 'Module Permissions',
          value: 'modulePermissions',
          class: 'table-header',
          width: '20%',
          sortable: false
        },
        {
          text: 'Action',
          value: 'action',
          width: '15%',
          align: 'end',
          class: 'table-header',
          sortable: false
        }
      ]
    }
  }
}
</script>

<style scoped>
.table-header {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.bord {
  border-top: thin solid rgba(0, 0, 0, 0.12);
}

.td_font {
  color: #262626;
  font-weight: 700;
}
.table-radius {
  border-radius: 0px !important;
}

.dark .td_font {
  color: #ffffff;
}
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
.v-data-table >>> .v-data-table__wrapper > table > thead > tr > th {
  font-size: 0.875rem;
  color: #000 !important;
}
</style>
